import { useEffect, useRef, useState } from 'react'
import type { Control, FieldValues, Path } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { env } from 'src/config/env'
import { logger } from 'src/lib/logger'
import SpeechBubbleIcon from 'src/shared/assets/images/speech-bubble.png'
import { ArrowUp } from 'src/shared/components/icons/arrow-up'
import PulsatingDots from 'src/shared/components/ui/animation/pulsating-dots-loader'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { CheckIcon } from 'src/shared/features/home/icons/check-icon'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useIsAppleDevice } from 'src/shared/hooks/use-device-detect'
import { useToast } from 'src/shared/hooks/use-toast'
import { formatToE164, phoneSchema } from 'src/shared/schemas/forms/consumer-auth'
import { cn } from 'src/shared/utils/cn'

import { useUserByUsername } from '../../api/accounts/use-user-by-username'
import { useProfessionalStartJourney } from '../../api/portfolio/use-professional-start-journey'

interface PhoneNumberDisclaimerProps {
  onClose?: () => void
}

function PhoneNumberDisclaimer({ onClose }: PhoneNumberDisclaimerProps) {
  const [showMore, setShowMore] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const onClickShowMore = () => {
    setShowMore(true)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={containerRef}
      className={cn(
        'px-[18px] py-5 text-center text-[12px] xs:text-[13px]',
        'rounded-[15px] bg-white text-primary shadow-[0_0_25px_0_rgba(27,48,41,0.1)]',
      )}
    >
      {!showMore ? (
        <p onClick={onClickShowMore} className="cursor-pointer leading-[1.6] text-primary">
          By entering your phone number you agree to our{' '}
          <Link to={env.LINK_TO_PRIVACY_POLICY} target="_blank">
            Privacy policy
          </Link>
          . <span className="text-primary underline">Read more</span>
        </p>
      ) : (
        <p>
          I confirm I am 18+ and consent to: (1) sharing my information with real estate professionals; (2) receiving
          communications from Conrad and these professionals via email, calls, and texts; and (3) the collection of my
          data as per the{' '}
          <Link to={env.LINK_TO_PRIVACY_POLICY} target="_blank" className="underline">
            Privacy Policy
          </Link>
          . I agree to the{' '}
          <Link to={env.LINK_TO_TERMS_OF_USE} target="_blank" className="underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to={env.LINK_TO_PRIVACY_POLICY} target="_blank" className="underline">
            Privacy Policy
          </Link>
          , which will be updated for consumer use. I can manage my preferences in settings.
        </p>
      )}
    </div>
  )
}

interface PhoneNumberFormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'currency' | 'date'
  onFocus?: () => void
  onBlur?: () => void
}

function PhoneNumberFormField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type,
  onFocus,
  onBlur,
}: PhoneNumberFormFieldProps<T>) {
  return (
    <InputFormField
      control={control}
      name={name}
      label={label}
      placeholder={placeholder}
      className="space-y-1 text-white"
      fieldClassName="text-white border-none xs:text-[16px] translate-y-[7px] placeholder:translate-y-0 bg-transparent px-5"
      floatingLabelClassName="peer-placeholder-shown:text-[16px] text-white/75 peer-focus:text-white/75 pointer-events-none bg-transparent -translate-y-[1px] px-4 peer-focus:px-4 peer-focus:-translate-y-[-3px] peer-placeholder-shown:translate-y-[-42%]"
      fieldContainerClassName="text-white bg-transparent rounded-[12px] h-[60px]"
      type={type}
      autoComplete="off"
      autoFocus={true}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  )
}

const phoneFormSchema = z.object({
  phone: phoneSchema,
})

type PhoneFormData = z.infer<typeof phoneFormSchema>

interface ProfileStartJourneyBtnProps {
  className?: string
  disclaimerPosition?: 'top' | 'bottom'
  variant?: 'default' | 'mobile'
  username: string
}

export default function ProfileStartJourneyBtn({
  className,
  disclaimerPosition = 'top',
  variant = 'default',
  username,
}: ProfileStartJourneyBtnProps) {
  const [step, setStep] = useState<'initial' | 'phone' | 'received'>('initial')
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [isPhoneNumberFocused, setIsPhoneNumberFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { mutateAsync: startProfessionalJourney } = useProfessionalStartJourney()
  const { isMobile } = useBreakpoint()
  const isIPhone = useIsAppleDevice()
  const { data: user } = useUserByUsername(username || '')
  const twilioPhoneNumber = user?.assignedTwilioPhoneNumber
  const firstName = user?.firstName
  const userId = user?.id
  const { toast } = useToast()

  const form = useForm<PhoneFormData>({
    defaultValues: {
      phone: '',
    },
  })

  const onClick = () => {
    setShowDisclaimer(true)
    if (isMobile && isIPhone && twilioPhoneNumber) {
      const smsNumber = twilioPhoneNumber
      const smsMessage = `Hi Lynk! I'm starting my real estate journey and need help from ${firstName}.`
      const smsUrl = `sms:${smsNumber}&body=${encodeURIComponent(smsMessage)}`
      window.location.href = smsUrl
    } else {
      if (step !== 'initial') {
        return
      }
      setStep('phone')
    }
  }

  const onClosePhoneNumberDisclaimer = () => {
    setShowDisclaimer(false)
  }

  const onSubmitPhoneNumberHandler = async (data: PhoneFormData) => {
    const parsedData = phoneFormSchema.safeParse(data)
    if (!parsedData.success) {
      toast({
        title: 'Error submitting phone number',
        description: parsedData.error.errors[0].message,
        variant: 'destructive',
      })
      return
    }
    const formattedPhoneNumber = formatToE164(data.phone)
    try {
      setIsSubmitting(true)
      if (!userId) {
        throw new Error('User ID is required')
      }
      const requestBody = {
        phoneNumber: formattedPhoneNumber,
        firstName: '',
        lastName: '',
        createdByUserId: userId,
      }
      await startProfessionalJourney(requestBody)
      setStep('received')
    } catch (error) {
      logger.error('Error submitting phone number:', error)
      toast({
        title: 'Error submitting phone number',
        description: 'There was an error submitting your phone number. Please try again later.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const shouldShowDisclaimer = isPhoneNumberFocused || showDisclaimer

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex h-[55px] cursor-pointer items-center justify-center rounded-full bg-primary px-[40px] text-[15px] text-white',
        step !== 'initial' && 'cursor-default pl-0 pr-[7px]',
        variant === 'mobile' && 'h-[48px] text-[14px]',
        className,
      )}
    >
      {step === 'initial' && (
        <div className="flex items-center gap-2">
          <img src={SpeechBubbleIcon} alt="Speech Bubble" className="size-[22px] xs:size-[24px]" />
          Start my journey
        </div>
      )}

      {step === 'phone' && (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmitPhoneNumberHandler)}
            className="relative flex w-full flex-row items-center justify-between"
          >
            <PhoneNumberFormField
              control={form.control}
              name="phone"
              label="Phone number"
              placeholder="Phone number"
              type="tel"
              onFocus={() => setIsPhoneNumberFocused(true)}
              onBlur={() => setIsPhoneNumberFocused(false)}
            />
            {isSubmitting ? (
              <div className="mr-2 flex items-center justify-center">
                <PulsatingDots className="size-[6px]" />
              </div>
            ) : (
              <Button
                type="submit"
                variant="default"
                className={cn(
                  'size-[44px] min-h-0 shrink-0 rounded-full bg-white px-0',
                  variant === 'mobile' && 'h-[34px] w-[34px]',
                )}
                // isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                <ArrowUp className="h-[14px] w-[12px] text-black xs:h-[16px] xs:w-[14px]" />
              </Button>
            )}
            <div
              className={cn(
                'absolute left-[50%] top-[calc(100%+14px)] w-full translate-x-[-48%] xs:w-[280px]',
                !shouldShowDisclaimer && 'hidden',
                disclaimerPosition === 'top' && 'bottom-[calc(100%+14px)] top-auto',
              )}
            >
              <PhoneNumberDisclaimer onClose={onClosePhoneNumberDisclaimer} />
            </div>
          </form>
        </Form>
      )}

      {step === 'received' && (
        <div className="mx-[18px] flex w-full flex-row items-center gap-[10px] rounded-full">
          <div
            className={cn(
              'flex h-[25px] min-h-0 w-[25px] flex-shrink-0 items-center justify-center rounded-full bg-white',
            )}
          >
            <CheckIcon className="text-primary" />
          </div>
          <div className="flex flex-col">
            <p className={cn('text-[11px] leading-[1.2] text-white/75', variant === 'mobile' && 'text-[11px]')}>
              Got it.
            </p>
            <p className={cn('text-[16px] leading-[1.2] text-white', variant === 'mobile' && 'text-[12px]')}>
              Check your texts!
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
